import { Typography } from '@tunasong/ui-lib'
import { Brain, Menu } from '@tunasong/icons'
import { isAIGenerated } from '@tunasong/models'
import type { TunaEditor } from '@tunasong/plugin-lib'
import { useEditorMessage } from '@tunasong/plugin-lib'
import type { CoreElement } from '@tunasong/schemas'
import cn from 'classnames'
import React, { useCallback, useState } from 'react'
import type { FC } from 'react'
import type { DragElementWrapper, DragSourceOptions } from 'react-dnd'
import { useReadOnly } from 'slate-react'
import styles from './block-gutter.module.css'
import { BlockMenu } from './block-menu.js'
import { BlockToolbar } from './block-toolbar.js'

interface BlockGutterProps {
  editor: TunaEditor
  element: CoreElement
  blockName: string
  hasFocus: boolean
  label?: string
  side?: 'left' | 'right'
  dragRef?: DragElementWrapper<DragSourceOptions>
}

export const BlockGutter: FC<BlockGutterProps> = ({ editor, element, blockName, hasFocus, label, side = 'left', dragRef }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [clickedAway, setClickedAway] = useState(0)
  const readOnly = useReadOnly()

  const showProperties = useEditorMessage({ editor, type: 'show-block-properties' })
  const handleShowProperties = useCallback(() => showProperties(element, anchorEl), [anchorEl, element, showProperties])

  const showComments = useEditorMessage({ editor, type: 'show-comments' })
  const handleShowComments = useCallback(() => showComments(element, anchorEl), [anchorEl, element, showComments])

  const handleMenu = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      if (anchorEl || Date.now() < clickedAway + 1000) {
        setAnchorEl(null)
        setClickedAway(0)
      } else {
        setAnchorEl(event.currentTarget)
      }
    },
    [anchorEl, clickedAway]
  )

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null)
    setClickedAway(Date.now())
  }, [])

  const handleMouseDown = useCallback(
    (e: React.SyntheticEvent) => {
      e.stopPropagation()
      // eslint-disable-next-line react-compiler/react-compiler -- Slate requires direct mutation of editor.selection
      editor.selection = null
    },
    [editor]
  )

  const menuContent = !readOnly ? (
    <BlockToolbar tabIndex={-1} ref={dragRef as never}>
      <button
        className={cn(styles.gutterButton, styles.menuButton)}
        data-block-id={element.id}
        onClick={handleMenu}
        onMouseDown={handleMouseDown}
        aria-label="Block menu"
        contentEditable={false}
      >
        <Menu size={24} className={styles.menuIcon} />
      </button>
    </BlockToolbar>
  ) : null

  const aiContent = isAIGenerated(element) ? (
    <div title="Assisted by Tunabrain creative AI" className={styles.aiBox}>
      <Brain className={styles.aiIcon} />
    </div>
  ) : null

  const labelContent = label ? (
    <Typography
      variant="body2"
      contentEditable={false}
      className={cn(styles.label, { [styles['label--focused']]: hasFocus })}
    >
      {label}
    </Typography>
  ) : null

  const isRight = side === 'right'

  if (readOnly) {
    return null
  }

  return (
    <>
      {/* Menu always on the left */}
      <div tabIndex={-1} title={blockName} contentEditable={false} aria-label="Gutter left" className={styles.gutter}>
        {menuContent}
        {!isRight && aiContent}
        {!isRight && labelContent}
      </div>
      {/* Label + AI badge on the right when side="right" */}
      {isRight && (aiContent || labelContent) ? (
        <div contentEditable={false} aria-label="Gutter right" className={cn(styles.gutter, styles['gutter--right'])}>
          {aiContent}
          {labelContent}
        </div>
      ) : null}
      {anchorEl ? (
        <BlockMenu
          open={true}
          anchorEl={anchorEl}
          block={element}
          onShowProperties={handleShowProperties}
          onComments={handleShowComments}
          onClose={handleCloseMenu}
        />
      ) : null}
    </>
  )
}
